import type { SceneStore } from '@os/doc';

/** What to edit in a task list: its title or one of its tasks. */
export type TaskEditTarget = 'title' | { itemId: string };
type Listener = (target: TaskEditTarget) => void;

/** Per store, per list: who opens that list's editor. Scoped to the store so a preview never opens the live board's tasks. */
const listeners = new WeakMap<SceneStore, Map<string, Set<Listener>>>();

/** A request made before the list rendered (a list just created): delivered when it mounts, if within `PENDING_MS`. */
const pending = new WeakMap<SceneStore, Map<string, { target: TaskEditTarget; at: number }>>();
const PENDING_MS = 2000;

/** Asks the list `listId` to edit its title or a task (the canvas controller resolves it; a native double-click never reaches it). */
export function requestTaskEdit(store: SceneStore, listId: string, target: TaskEditTarget): void {
  const set = listeners.get(store)?.get(listId);
  if (set?.size) return set.forEach((l) => l(target));
  let byList = pending.get(store);
  if (!byList) pending.set(store, (byList = new Map()));
  byList.set(listId, { target, at: Date.now() });
}

export function onTaskEditRequest(store: SceneStore, listId: string, l: Listener): () => void {
  let byList = listeners.get(store);
  if (!byList) listeners.set(store, (byList = new Map()));
  let set = byList.get(listId);
  if (!set) byList.set(listId, (set = new Set()));
  set.add(l);
  const waiting = pending.get(store)?.get(listId);
  if (waiting) {
    pending.get(store)!.delete(listId);
    if (Date.now() - waiting.at <= PENDING_MS) l(waiting.target);
  }
  return () => {
    set.delete(l);
    if (!set.size) byList.delete(listId);
  };
}
